import axios from 'axios';

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Remove console.logs in production
if (process.env.NODE_ENV !== 'development') {
  console.log = () => {};
}

// Fetch questions with optional status filter
export const fetchQuestions = async (filter = {}) => {
  try {
    const response = await axios.get('/api/questions', {
      params: filter,
      headers: getAuthHeaders(),
    });

    const questions = response.data;
    if (!questions?.length) {
      console.warn('No questions returned from the server.');
      return [];
    }

    // Map Postgres field names to expected frontend format
    const mappedQuestions = questions.map(q => ({
      question_id: q.question_id,
      _id: q.question_id, // Alias for frontend compatibility
      questionText: q.question_text,
      options: q.options,
      answerType: q.answer_type,
      category: q.category,
      subCategory: q.sub_category,
      facet: q.facet,
      facetNote: q.facet_note,
      exampleInfo: q.example_info,
      taxonomy: q.taxonomy,
      status: q.status,
    }));

    if (process.env.NODE_ENV !== 'production') {
      console.log('Fetched questions:', { count: mappedQuestions.length });
      const invalidQuestions = mappedQuestions.filter(q => !q._id || !q.questionText);
      if (invalidQuestions.length) {
        console.warn('Invalid questions detected:', { count: invalidQuestions.length });
      }
    }

    return mappedQuestions;
  } catch (error) {
    console.error('Fetch questions error:', {
      message: error.message,
      status: error.response?.status,
    });
    throw new Error(`Failed to fetch questions: ${error.response?.data?.message || error.message}`);
  }
};

// Fetch user progress
export const fetchProgress = async (userId, { assessmentType, sessionId, fetchLatest = false, isParentView = false } = {}) => {
  try {
    // Choose endpoint based on view mode
    const endpoint = isParentView
      ? `/api/assessment/child-progress/${userId}`
      : `/api/assessment/progress/${userId}`;

    const params = { assessmentType };
    if (sessionId) params.sessionId = sessionId;
    if (fetchLatest) params.fetchLatest = 'true';

    const response = await axios.get(endpoint, {
      headers: getAuthHeaders(),
      params,
    });

    const progress = response.data;
    if (!progress?.sessionId) {
      return null;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('Fetched progress:', { userId, assessmentType, sessionId, isParentView });
    }

    return progress;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Fetch progress error:', {
      userId,
      status: error.response?.status,
      isParentView,
    });
    throw new Error(`Failed to fetch progress: ${error.response?.data?.message || error.message}`);
  }
};

// Submit multiple answers in a batch
export const submitAnswersBatch = async (userId, sessionId, answers, assessmentType, questionOptionsMap) => {
  try {
    const payload = {
      userId,
      assessmentType,
      sessionId,
      answers: answers.map(answer => ({
        questionId: answer.questionId,
        category: answer.category,
        subCategory: answer.subCategory,
        facet: answer.facet,
        taxonomy: answer.taxonomy,
        score: answer.score,
      })),
    };

    const response = await axios.post(`/api/assessment/submit-batch`, payload, {
      headers: getAuthHeaders(),
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('submitAnswersBatch: Response:', {
        insertedCount: response.data.insertedCount,
        assessmentType,
        answersCount: answers.length,
      });
    }

    return response.data;
  } catch (error) {
    console.error('Submit batch answers error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
    });
    throw new Error(`Failed to submit batch answers: ${error.response?.data?.message || error.message}`);
  }
};

// Save user progress
export const saveProgress = async (progressData) => {
  try {
    if (!progressData.userId) throw new Error('User ID is required');
    if (!progressData.sessionId) throw new Error('Session ID is required');
    if (!progressData.assessmentType) throw new Error('Assessment type is required');

    const payload = {
      userId: progressData.userId,
      sessionId: progressData.sessionId,
      assessmentType: progressData.assessmentType,
      topSubcategories: progressData.topSubcategories || progressData.topFacets || [],
      lastCompletionDate: progressData.lastCompletionDate || null,
    };

    if (process.env.NODE_ENV !== 'production') {
      console.log('saveProgress: Sending progress data:', {
        userId: payload.userId,
        sessionId: payload.sessionId,
        assessmentType: payload.assessmentType,
        topSubcategories: payload.topSubcategories,
        lastCompletionDate: payload.lastCompletionDate,
      });
    }

    const response = await axios.post(`/api/assessment/save-progress`, payload, {
      headers: getAuthHeaders(),
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('saveProgress: Successfully saved progress:', {
        sessionId: response.data.sessionId,
        lastCompletionDate: response.data.lastCompletionDate,
      });
    }

    return response.data;
  } catch (error) {
    console.error('saveProgress: Error saving progress:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Unauthorized: Please log in again');
    }
    throw new Error(`Failed to save progress: ${error.response?.data?.message || error.message}`);
  }
};

// Fetch raw answers for Part II
export const fetchRawAnswers = async (userId, sessionId, assessmentType, isParentView = false) => {
  try {
    // Choose endpoint based on view mode
    const endpoint = isParentView
      ? `/api/assessment/child-visualizations/${userId}`
      : `/api/assessment/visualizations/${userId}`;

    const response = await axios.get(endpoint, {
      params: { sessionId, assessmentType },
      headers: getAuthHeaders(),
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Fetched raw answers:', { userId, sessionId, assessmentType, isParentView });
    }

    return response.data;
  } catch (error) {
    console.error('Fetch raw answers error:', {
      status: error.response?.status,
      userId,
      sessionId,
      assessmentType,
      isParentView,
    });
    throw error;
  }
};

// Delete previous assessment data
export const deletePreviousAssessment = async (userId, assessmentType, retries = 2) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await axios.delete(`/api/assessment/delete`, {
        headers: getAuthHeaders(),
        data: { userId, assessmentType },
      });

      const responseData = response.data;

      if (response.status === 200) {
        console.log('Delete response:', responseData);
        return responseData;
      }

      throw new Error(responseData.message || 'Failed to delete previous assessment data');
    } catch (error) {
      console.error(`Delete attempt ${attempt} failed:`, {
        message: error.message,
        status: error.response?.status,
        response: error.response?.data,
      });

      if (error.response?.status === 404 || error.response?.data?.message === 'No previous assessment data found') {
        console.warn('No previous assessment data found:', error.response?.data?.message || '404 Not Found');
        return { message: 'No previous assessment data found' };
      }

      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        continue;
      }

      throw new Error(`Failed to delete previous assessment data: ${error.response?.data?.message || error.message}`);
    }
  }
};

// Generate insights
/*export const generateInsights = async (userId, payload) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!payload) throw new Error('Payload is required');

    console.log('generateInsights: Preparing request', {
      userId,
      payloadKeys: Object.keys(payload),
      headers: getAuthHeaders(),
    });

    const response = await axios.post(
      '/api/insights/generate',
      { userId, payload },
      {
        headers: getAuthHeaders(),
        timeout: 30000,
      }
    );

    console.log('generateInsights: Received response', {
      status: response.status,
      data: response.data,
    });

    if (!response.data) {
      throw new Error('No data in response from insights API');
    }

    return response;
  } catch (error) {
    console.error('generateInsights: Error details', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      code: error.code,
      config: error.config,
    });

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Unauthorized: Please log in again');
    }

    throw new Error(`Failed to generate insights: ${error.message}`);
  }
};

// Save insights
export const saveInsights = async (userId, insights, generationDate) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!insights) throw new Error('Insights are required');
    if (!generationDate) throw new Error('Generation date is required');

    const payload = { userId, insights, generationDate };

    if (process.env.NODE_ENV !== 'production') {
      console.log('saveInsights: Sending insights data:', {
        userId,
        insightsLength: insights.length,
        generationDate,
      });
    }

    const response = await axios.post(`/api/insights/save`, payload, {
      headers: getAuthHeaders(),
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('saveInsights: Successfully saved insights:', {
        userId,
        generationDate: response.data.insights.generation_date,
      });
    }

    return response.data;
  } catch (error) {
    console.error('saveInsights: Error saving insights:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Unauthorized: Please log in again');
    }
    throw new Error(`Failed to save insights: ${error.response?.data?.message || error.message}`);
  }
};*/

// Fetch insights
/*export const fetchInsights = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const response = await axios.get(`/api/insights/${userId}`, {
      headers: getAuthHeaders(),
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('fetchInsights: Successfully fetched insights:', {
        userId,
        hasInsights: !!response.data.insights,
        generationDate: response.data.generationDate,
      });
    }

    return response.data;
  } catch (error) {
    console.error('fetchInsights: Error fetching insights:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};*/



// Delete previous insights
/*export const deletePreviousInsights = async (userId, retries = 2) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await axios.delete(`/api/insights/${userId}`, {
        headers: getAuthHeaders(),
      });

      const responseData = response.data;

      if (response.status === 200) {
        console.log('Delete insights response:', responseData);
        return responseData;
      }

      throw new Error(responseData.message || 'Failed to delete previous insights');
    } catch (error) {
      console.error(`Delete insights attempt ${attempt} failed:`, {
        message: error.message,
        status: error.response?.status,
        response: error.response?.data,
      });

      if (error.response?.status === 404 || error.response?.data?.message === 'No insights found') {
        console.warn('No previous insights found:', error.response?.data?.message || '404 Not Found');
        return { message: 'No previous insights found' };
      }

      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        continue;
      }

      throw new Error(`Failed to delete previous insights: ${error.response?.data?.message || error.message}`);
    }
  }
};*/

// Reset password
export const resetPassword = async (email, token, newPassword) => {
  try {
    if (!email) throw new Error('Email is required');
    if (!token) throw new Error('Reset token is required');
    if (!newPassword) throw new Error('New password is required');

    const payload = { email, token, newPassword };

    if (process.env.NODE_ENV !== 'production') {
      console.log('resetPassword: Sending reset password request:', { email });
    }

    const response = await axios.post(`/api/users/reset-password`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('resetPassword: Successfully reset password:', { email });
    }

    return response.data;
  } catch (error) {
    console.error('resetPassword: Error resetting password:', {
      status: error.response?.status,
      message: error.response?.data?.error || error.message,
    });
    throw new Error(`Failed to reset password: ${error.response?.data?.error || error.message}`);
  }
};

const assessmentApi = {
  fetchQuestions,
  submitAnswersBatch,
  fetchProgress,
  saveProgress,
  deletePreviousAssessment,
  fetchRawAnswers,
  resetPassword,
};

export default assessmentApi;